import { useUserPreferences } from "@/hooks/useUserPreferences";
import { CHART_COLOR_VARIANTS, CHART_WEIGHT_VARIANTS } from "@/lib/chart-prefs";
import type { ChartColors } from "@/types/users";

import { ChartPreview } from "./ChartPreview";
import { OptionTile } from "./OptionTile";
import { PreferenceCard } from "./PreferenceCard";

const TERMS = [12, 24, 36, 48, 60] as const;

/**
 * Each colour choice's own stops, independent of the one in force: Default
 * reads the brand stops as `<html>` resolves them (the page's are re-pointed
 * while high contrast is on), High contrast its mode-resolved variants
 * (`index.css` § "Chart colors").
 */
const COLOR_PREVIEW: Record<ChartColors, { colors: string[]; grid: string }> = {
  default: {
    colors: TERMS.map((t) => `var(--chart-default-term-${t})`),
    grid: "var(--border)",
  },
  high_contrast: {
    colors: TERMS.map((t) => `var(--chart-hc-term-${t})`),
    grid: "var(--border-strong)",
  },
};

/** The page's own series colours — whichever palette is in force. */
const CURRENT_COLORS = TERMS.map((t) => `var(--market-term-${t})`);

/**
 * Preferences → Charts: colours and line weight. Every tile is a miniature
 * chart drawn the way the choice draws — the colour tiles at the current
 * weight, the weight tiles in the current colours — so the two combine the
 * way the real charts will.
 */
export function ChartsSection() {
  const { effective, save, readOnly } = useUserPreferences();

  return (
    <div className="flex flex-col gap-4">
      <PreferenceCard
        title="Colors"
        description="High contrast keeps every series its usual color, deepened on light and brightened on dark so each line stands clear of the background."
      >
        <div role="radiogroup" aria-label="Chart colors" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CHART_COLOR_VARIANTS.map((v) => (
            <OptionTile
              key={v.id}
              selected={effective.chart_colors === v.id}
              disabled={readOnly}
              onSelect={() => save({ chart_colors: v.id })}
              preview={<ChartPreview colors={COLOR_PREVIEW[v.id].colors} grid={COLOR_PREVIEW[v.id].grid} />}
              label={v.label}
              description={v.description}
            />
          ))}
        </div>
      </PreferenceCard>

      <PreferenceCard title="Line weight" description="How thick every chart's data lines draw.">
        <div role="radiogroup" aria-label="Line weight" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CHART_WEIGHT_VARIANTS.map((v) => (
            <OptionTile
              key={v.id}
              selected={effective.chart_line_weight === v.id}
              disabled={readOnly}
              onSelect={() => save({ chart_line_weight: v.id })}
              preview={<ChartPreview colors={CURRENT_COLORS} weightClass={v.className} />}
              label={v.label}
              description={v.description}
            />
          ))}
        </div>
      </PreferenceCard>
    </div>
  );
}
